// Import `useSelector` from react-redux (not the typed re-export in `Flux/index`): pulling
// `Flux/index` would drag the whole slice aggregation into this low-level hook and create a
// module init cycle (it is imported, transitively, by SINK_REGISTRY). `State` is a type-only
// import and is erased at runtime, so it is cycle-safe.
import { useSelector } from 'react-redux'
import type { State } from '../../../../Flux/Slice/index.js'
import type {
  SourceId,
  SourceSharedState,
  SourceSnapshot,
  SourceStatus,
  VizSourceEntity
} from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'

export interface SourceView {
  src: VizSourceEntity | null
  snapshot: SourceSnapshot | null
  shared: SourceSharedState | undefined
  status: SourceStatus
}

/**
 * The canonical way for a sink/source component to read its source from the store.
 *
 * Selects the source entity ONCE (a stable store reference) and derives the commonly-needed
 * fields. This replaces the `useSelector(s => ({ snapshot, shared, status }))` pattern that was
 * copy-pasted across ~10 components — which returned a fresh object every call and so re-rendered
 * every sink on every dispatched action (websocket pings, message removals, …). Selecting only the
 * entity keeps re-renders scoped to genuine source changes.
 *
 * `shared` is left `undefined` when the source has none yet; callers optional-chain (`shared?.…`).
 */
export function useSourceView(sourceId: SourceId): SourceView {
  const src = useSelector((s: typeof State) => s.visualization.sources[sourceId] ?? null)
  return {
    src,
    snapshot: src?.snapshot ?? null,
    shared: src?.shared,
    status: src?.status ?? 'init'
  }
}

export interface KpiMode {
  /** True when a trait GROUP overlay is active (and no single trait) → compose by trait instead of taxon.
   *  A single [traitName] wins and routes back to the NORMAL taxa transformer over a trait-filtered cube
   *  (the filter is applied upstream in `selectFilteredCube`), so `inTraitMode` is false in that case. */
  inTraitMode: boolean
  /** The active trait group, or `''` when none. Always a string so it slots straight into the
   *  trait transformer opts (which the sinks only invoke when `inTraitMode`). */
  traitGroup: string
  /** A single exact trait to filter the cube to, or `''` when none. */
  traitName: string
  noTraitMode: 'include' | 'exclude'
}

/**
 * Derives the Nature-KPI / trait-mode view of a source's shared state. Used by every
 * composition sink (StackedBar/Area/Line/Pie/Treemap) to decide between the taxa and trait
 * transformers, so the branch logic isn't copy-pasted.
 */
export function kpiMode(shared: SourceSharedState | undefined): KpiMode {
  const traitGroup = shared?.natureKPIs?.traitGroup ?? ''
  const traitName = shared?.natureKPIs?.traitName ?? ''
  return {
    traitGroup,
    traitName,
    // A single trait wins: it filters the cube (upstream) and renders as a normal taxonomic chart, so we
    // must NOT route to the trait-composition transformer.
    inTraitMode: traitGroup !== '' && traitName === '',
    noTraitMode: shared?.natureKPIs?.noTraitMode ?? 'include'
  }
}
